<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useReportMetrics } from 'dashboard/composables/useReportMetrics';
import fromUnixTime from 'date-fns/fromUnixTime';
import getHours from 'date-fns/getHours';
import format from 'date-fns/format';
import { formatTime } from '@chatwoot/utils';
import { clampDataBetweenTimeline } from 'shared/helpers/ReportsDataHelper';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';

import ReportHeader from './components/ReportHeader.vue';
import ReportFilters from './components/ReportFilters.vue';
import DashboardStatCard from './components/DashboardStatCard.vue';
import Table from 'dashboard/components/table/Table.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import LineChart from 'shared/components/charts/LineChart.vue';
import DoughnutChart from 'shared/components/charts/DoughnutChart.vue';
import ReportsAPI from 'dashboard/api/reports';
import FlowBuilderAPI from 'dashboard/api/flowBuilder';

const { t } = useI18n();
const store = useStore();
const { isAdmin } = useAdmin();
const { calculateTrend } = useReportMetrics();

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);

const accountSummary = useMapGetter('getAccountSummary');
const accountConversationMetric = useMapGetter('getAccountConversationMetric');
const hourlyHeatmapData = useMapGetter('getAccountConversationHeatmapData');
const overviewUIFlags = useMapGetter('getOverviewUIFlags');
const agentSummaryReports = useMapGetter(
  'summaryReports/getAgentSummaryReports'
);
const teamSummaryReports = useMapGetter(
  'summaryReports/getTeamSummaryReports'
);
const labelSummaryReports = useMapGetter(
  'summaryReports/getLabelSummaryReports'
);
const summaryReportsUIFlags = useMapGetter('summaryReports/getUIFlags');
const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');
const labels = useMapGetter('labels/getLabels');
const flows = useMapGetter('flowBuilder/getFlows');

const isLoadingBreakdown = computed(
  () =>
    summaryReportsUIFlags.value.isFetchingAgentSummaryReports ||
    summaryReportsUIFlags.value.isFetchingTeamSummaryReports ||
    summaryReportsUIFlags.value.isFetchingLabelSummaryReports
);

// Hourly series reuses the existing conversation-heatmap pipeline
// (fetchAccountConversationHeatmap / getAccountConversationHeatmapData —
// already clamped to [from, to)). The daily series has no equivalent
// collision-free store slice: `fetchAccountReport` keys accountReport.data
// only by metric name, so fetching conversations_count with groupBy=day
// would clobber the hourly one if both went through that action. Fetched
// locally here instead, clamped the same way the store action does.
const dailySeries = ref([]);
const isLoadingDailyChart = ref(false);

const fetchDailySeries = async () => {
  isLoadingDailyChart.value = true;
  try {
    const { data } = await ReportsAPI.getReports({
      metric: 'conversations_count',
      from: from.value,
      to: to.value,
      groupBy: 'day',
      businessHours: businessHours.value,
    });
    dailySeries.value = clampDataBetweenTimeline(data, from.value, to.value);
  } catch {
    useAlert(t('REPORT.DATA_FETCHING_FAILED'));
  } finally {
    isLoadingDailyChart.value = false;
  }
};

const fetchAll = () => {
  const payload = {
    from: from.value,
    to: to.value,
    businessHours: businessHours.value,
  };
  store.dispatch('fetchAccountSummary', payload);
  store.dispatch('fetchAccountConversationMetric');
  // metric is required server-side (every other caller of this action --
  // e.g. ConversationHeatmapContainer.vue -- passes it via BaseHeatmapContainer);
  // without it the request errors and, since the action has no .catch, the
  // loading flag was getting stuck true forever.
  store.dispatch('fetchAccountConversationHeatmap', {
    ...payload,
    metric: 'conversations_count',
  });
  store.dispatch('summaryReports/fetchAgentSummaryReports', {
    since: from.value,
    until: to.value,
    businessHours: businessHours.value,
  });
  store.dispatch('summaryReports/fetchTeamSummaryReports', {
    since: from.value,
    until: to.value,
    businessHours: businessHours.value,
  });
  store.dispatch('summaryReports/fetchLabelSummaryReports', {
    since: from.value,
    until: to.value,
    businessHours: businessHours.value,
  });
  fetchDailySeries();
  if (selectedFlowId.value) fetchMenuAnalytics();
};

onMounted(() => {
  // teams/labels are already fetched by the persistent Sidebar on app boot.
  store.dispatch('agents/get');
  // Flow Builder is administrator-only end to end (FlowPolicy/
  // FlowExecutionPolicy) — never dispatch it for report_manage-only users,
  // or the resulting 401 trips the global session-expiry redirect.
  if (isAdmin.value) store.dispatch('flowBuilder/get');
});

const onFilterChange = updatedFilter => {
  from.value = updatedFilter.from;
  to.value = updatedFilter.to;
  businessHours.value = updatedFilter.businessHours;
  fetchAll();
};

// ── KPI cards ────────────────────────────────────────────────────────────────
// Trend = % change vs the previous period, reusing the same composable/logic
// as ChartStats.vue (dashboard/composables/useReportMetrics.js). Only shown
// where a "previous period" comparison actually makes sense — not for live
// snapshots (open conversations) or derived-from-range values (peak hour,
// avg/hour, since a differently-sized previous period isn't comparable).

// Same "% change vs previous" arithmetic as useReportMetrics#calculateTrend,
// reused here for derived values (a ratio, a sum of two fields) that don't
// map to a single accountSummary key so calculateTrend(key) can't read them
// directly. previous === 0 is handled explicitly: going from a real zero
// baseline to any positive value is a genuine improvement, not "no trend" —
// swallowing it (returning 0) would hide a 0%→75% resolution-rate jump.
const percentChange = (current, previous) => {
  if (!previous) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
};

const resolutionRateValue = summary => {
  if (!summary.conversations_count) return 0;
  return ((summary.resolutions_count || 0) / summary.conversations_count) * 100;
};

const resolutionRate = computed(
  () => `${Math.round(resolutionRateValue(accountSummary.value))}%`
);

const resolutionRateTrend = computed(() => {
  const previous = accountSummary.value.previous || {};
  if (!previous.conversations_count) return 0; // no previous period to compare
  return percentChange(
    resolutionRateValue(accountSummary.value),
    resolutionRateValue(previous)
  );
});

const totalMessages = computed(() => {
  const { incoming_messages_count: incoming, outgoing_messages_count: out } =
    accountSummary.value;
  return ((incoming || 0) + (out || 0)).toLocaleString();
});

const totalMessagesTrend = computed(() => {
  const previous = accountSummary.value.previous || {};
  const previousTotal =
    (previous.incoming_messages_count || 0) +
    (previous.outgoing_messages_count || 0);
  const currentTotal =
    (accountSummary.value.incoming_messages_count || 0) +
    (accountSummary.value.outgoing_messages_count || 0);
  return percentChange(currentTotal, previousTotal);
});

const peakHour = computed(() => {
  if (!hourlyHeatmapData.value.length) return '--';
  const totalsByHour = Array(24).fill(0);
  hourlyHeatmapData.value.forEach(point => {
    const hour = getHours(fromUnixTime(point.timestamp));
    totalsByHour[hour] += point.value || 0;
  });
  const maxHour = totalsByHour.indexOf(Math.max(...totalsByHour));
  return `${String(maxHour).padStart(2, '0')}:00`;
});

const avgPerHour = computed(() => {
  const total = accountSummary.value.conversations_count || 0;
  const hours = Math.max(1, Math.round((to.value - from.value) / 3600));
  return (total / hours).toFixed(1);
});

const totalConversationsSparkline = computed(() =>
  dailySeries.value.slice(-7).map(point => point.value)
);

// ── Charts ───────────────────────────────────────────────────────────────────

const hourlyChartCollection = computed(() => ({
  labels: hourlyHeatmapData.value.map(point =>
    format(fromUnixTime(point.timestamp), 'dd-MMM HH:00')
  ),
  datasets: [
    {
      type: 'bar',
      label: t('CUSTOM_DASHBOARD.HOURLY_CHART_TITLE'),
      data: hourlyHeatmapData.value.map(point => point.value),
      backgroundColor: '#00B5A9',
    },
  ],
}));

const dailyChartCollection = computed(() => ({
  labels: dailySeries.value.map(point =>
    format(fromUnixTime(point.timestamp), 'dd-MMM')
  ),
  datasets: [
    {
      type: 'line',
      label: t('CUSTOM_DASHBOARD.DAILY_CHART_TITLE'),
      data: dailySeries.value.map(point => point.value),
      borderColor: '#00B5A9',
      backgroundColor: 'rgba(0, 181, 169, 0.15)',
      pointBackgroundColor: '#00B5A9',
      tension: 0.3,
      fill: true,
    },
  ],
}));

// ── Breakdown tables (agent/team/label) ────────────────────────────────────
// Deliberately not <SummaryReports> here — that component ships its own
// date-range filter bar, which would triplicate the page-level ReportFilters
// above it. Same store data (summaryReports module), lighter presentation.

const columnHelper = createColumnHelper();
// computed, not a plain const: a top-level `t()` call in <script setup> only
// ever evaluates once, so if the real locale loads/switches after this runs
// the header would be stuck on whatever was active at that instant (English,
// in practice) forever -- unlike the template's own $t() calls, which are
// reactive. useVueTable's `columns` below is passed as a getter so it re-reads
// this on every locale change too.
const breakdownColumns = computed(() => [
  columnHelper.accessor('name', {
    header: t('CUSTOM_DASHBOARD.TABLE.NAME'),
  }),
  columnHelper.accessor('conversationsCount', {
    header: t('SUMMARY_REPORTS.CONVERSATIONS'),
  }),
  columnHelper.accessor('resolvedConversationsCount', {
    header: t('SUMMARY_REPORTS.RESOLUTION_COUNT'),
  }),
]);

const buildBreakdownRows = (entities, metrics) => {
  const metricsById = new Map(metrics.map(m => [m.id, m]));
  return entities.map(entity => {
    const m = metricsById.get(entity.id) || {};
    const conversationsCountRaw = m.conversationsCount || 0;
    return {
      id: entity.id,
      name: entity.name ?? entity.title,
      color: entity.color, // present on labels only; undefined elsewhere
      conversationsCountRaw,
      conversationsCount: conversationsCountRaw.toLocaleString(),
      resolvedConversationsCount: (
        m.resolvedConversationsCount || 0
      ).toLocaleString(),
    };
  });
};

const agentRows = computed(() =>
  buildBreakdownRows(agents.value, agentSummaryReports.value)
);
const teamRows = computed(() =>
  buildBreakdownRows(teams.value, teamSummaryReports.value)
);
const labelRows = computed(() =>
  buildBreakdownRows(labels.value, labelSummaryReports.value)
);

const agentTable = useVueTable({
  get data() {
    return agentRows.value;
  },
  get columns() {
    return breakdownColumns.value;
  },
  getCoreRowModel: getCoreRowModel(),
});
const teamTable = useVueTable({
  get data() {
    return teamRows.value;
  },
  get columns() {
    return breakdownColumns.value;
  },
  getCoreRowModel: getCoreRowModel(),
});

// ── Label breakdown: doughnut (real tag colors) + clickable legend, filters
// the table below. Modest, dependency-free version of BI cross-filtering —
// local component state only, no new store/router wiring.

const selectedLabelId = ref(null);
const toggleLabelFilter = id => {
  selectedLabelId.value = selectedLabelId.value === id ? null : id;
};

// Largest-remainder rounding: round every share down first, then hand the
// leftover percentage points to whichever rows lost the most to flooring —
// guarantees the legend always sums to exactly 100%, unlike rounding each
// row independently (e.g. 3 equal thirds would show 33/33/33 = 99%).
const normalizedPercentages = (values, total) => {
  if (!total) return values.map(() => 0);
  const raw = values.map(value => (value / total) * 100);
  const floors = raw.map(Math.floor);
  const remainder = 100 - floors.reduce((sum, value) => sum + value, 0);
  const byRemainingFraction = raw
    .map((value, index) => ({ index, fraction: value - floors[index] }))
    .sort((a, b) => b.fraction - a.fraction);
  const result = [...floors];
  for (let i = 0; i < remainder; i += 1) {
    result[byRemainingFraction[i].index] += 1;
  }
  return result;
};

const labelRowsWithShare = computed(() => {
  const counts = labelRows.value.map(row => row.conversationsCountRaw);
  const total = counts.reduce((sum, value) => sum + value, 0);
  const percentages = normalizedPercentages(counts, total);
  return labelRows.value.map((row, index) => ({
    ...row,
    percentage: percentages[index],
  }));
});

const filteredLabelRows = computed(() =>
  selectedLabelId.value
    ? labelRows.value.filter(row => row.id === selectedLabelId.value)
    : labelRows.value
);

const selectedLabelName = computed(
  () => labelRows.value.find(row => row.id === selectedLabelId.value)?.name
);

const labelTable = useVueTable({
  get data() {
    return filteredLabelRows.value;
  },
  get columns() {
    return breakdownColumns.value;
  },
  getCoreRowModel: getCoreRowModel(),
});

// Doughnut segment order/index always matches labelRowsWithShare (both derive
// straight from labelRows with no independent sort/filter) — the click
// handler below relies on that to map a clicked slice back to its row.
const labelDoughnutCollection = computed(() => {
  const rows = labelRowsWithShare.value;
  return {
    labels: rows.map(row => row.name),
    datasets: [
      {
        data: rows.map(row => row.conversationsCountRaw),
        backgroundColor: rows.map(row => {
          const baseColor = row.color || '#64748B';
          if (!selectedLabelId.value) return baseColor;
          // Dim every segment except the active filter for in-chart feedback.
          return row.id === selectedLabelId.value ? baseColor : `${baseColor}33`;
        }),
        borderWidth: 0,
      },
    ],
  };
});

// Not reactive on purpose — the onClick closure reads selectedLabelId/
// labelRowsWithShare live via .value on every click, so a stable object
// identity is enough; no need to recreate it on every selection change.
const labelDoughnutOptions = {
  onClick: (_event, elements) => {
    if (!elements.length) return;
    const row = labelRowsWithShare.value[elements[0].index];
    if (row) toggleLabelFilter(row.id);
  },
};

// ── Menu/URA analytics (administrator-only, mirrors Flow Builder access) ───

const selectedFlowId = ref('');
const menuAnalytics = ref([]);
const isLoadingMenuAnalytics = ref(false);
let flowRequestId = 0;

const fetchMenuAnalytics = async () => {
  const requestId = ++flowRequestId;
  if (!selectedFlowId.value) {
    menuAnalytics.value = [];
    return;
  }

  isLoadingMenuAnalytics.value = true;
  try {
    const { data } = await FlowBuilderAPI.getMenuAnalytics(
      selectedFlowId.value,
      { since: from.value, until: to.value }
    );
    if (requestId !== flowRequestId) return; // a newer flow/filter change won the race
    menuAnalytics.value = data.payload || [];
  } catch {
    if (requestId === flowRequestId) useAlert(t('REPORT.DATA_FETCHING_FAILED'));
  } finally {
    if (requestId === flowRequestId) isLoadingMenuAnalytics.value = false;
  }
};

const menuNodeChartCollection = node => ({
  labels: node.options.map(opt => opt.label || opt.key),
  datasets: [
    {
      type: 'bar',
      label: node.node_label,
      data: node.options.map(opt => opt.count),
      backgroundColor: '#00B5A9',
    },
  ],
});
</script>

<template>
  <ReportHeader
    :header-title="$t('CUSTOM_DASHBOARD.HEADER')"
    :header-description="$t('CUSTOM_DASHBOARD.DESCRIPTION')"
  />

  <ReportFilters
    :show-entity-filter="false"
    :show-group-by="false"
    @filter-change="onFilterChange"
  />

  <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
    <DashboardStatCard
      category="volume"
      icon="i-lucide-message-circle"
      :label="$t('CUSTOM_DASHBOARD.METRICS.TOTAL_CONVERSATIONS.NAME')"
      :value="(accountSummary.conversations_count || 0).toLocaleString()"
      :info-text="$t('CUSTOM_DASHBOARD.METRICS.TOTAL_CONVERSATIONS.INFO_TEXT')"
      :trend="calculateTrend('conversations_count')"
      :sparkline="totalConversationsSparkline"
    />
    <DashboardStatCard
      category="neutral"
      icon="i-lucide-inbox"
      :label="$t('CUSTOM_DASHBOARD.METRICS.OPEN_CONVERSATIONS.NAME')"
      :value="(accountConversationMetric.open || 0).toLocaleString()"
      :info-text="$t('CUSTOM_DASHBOARD.METRICS.OPEN_CONVERSATIONS.INFO_TEXT')"
    />
    <DashboardStatCard
      category="rate"
      icon="i-lucide-percent"
      :label="$t('CUSTOM_DASHBOARD.METRICS.RESOLUTION_RATE.NAME')"
      :value="resolutionRate"
      :info-text="$t('CUSTOM_DASHBOARD.METRICS.RESOLUTION_RATE.INFO_TEXT')"
      :trend="resolutionRateTrend"
    />
    <DashboardStatCard
      category="time"
      icon="i-lucide-timer"
      :label="$t('REPORT.METRICS.FIRST_RESPONSE_TIME.NAME')"
      :value="formatTime(accountSummary.avg_first_response_time || 0)"
      :info-text="$t('REPORT.METRICS.FIRST_RESPONSE_TIME.INFO_TEXT')"
      :trend="calculateTrend('avg_first_response_time')"
      trend-good-direction="down"
    />
    <DashboardStatCard
      category="time"
      icon="i-lucide-check-circle-2"
      :label="$t('REPORT.METRICS.RESOLUTION_TIME.NAME')"
      :value="formatTime(accountSummary.avg_resolution_time || 0)"
      :info-text="$t('REPORT.METRICS.RESOLUTION_TIME.INFO_TEXT')"
      :trend="calculateTrend('avg_resolution_time')"
      trend-good-direction="down"
    />
    <DashboardStatCard
      category="volume"
      icon="i-lucide-mail"
      :label="$t('CUSTOM_DASHBOARD.METRICS.TOTAL_MESSAGES.NAME')"
      :value="totalMessages"
      :info-text="$t('CUSTOM_DASHBOARD.METRICS.TOTAL_MESSAGES.INFO_TEXT')"
      :trend="totalMessagesTrend"
    />
    <DashboardStatCard
      category="neutral"
      icon="i-lucide-flame"
      :label="$t('CUSTOM_DASHBOARD.METRICS.PEAK_HOUR.NAME')"
      :value="peakHour"
      :info-text="$t('CUSTOM_DASHBOARD.METRICS.PEAK_HOUR.INFO_TEXT')"
    />
    <DashboardStatCard
      category="neutral"
      icon="i-lucide-activity"
      :label="$t('CUSTOM_DASHBOARD.METRICS.AVG_PER_HOUR.NAME')"
      :value="avgPerHour"
      :info-text="$t('CUSTOM_DASHBOARD.METRICS.AVG_PER_HOUR.INFO_TEXT')"
    />
  </div>

  <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
    <div
      class="p-4 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-sm font-medium text-n-slate-11 mb-2">
        {{ $t('CUSTOM_DASHBOARD.HOURLY_CHART_TITLE') }}
      </h3>
      <div class="h-72">
        <woot-loading-state
          v-if="overviewUIFlags.isFetchingAccountConversationsHeatmap"
          class="text-xs"
        />
        <template v-else>
          <BarChart
            v-if="hourlyHeatmapData.length"
            :collection="hourlyChartCollection"
          />
          <span v-else class="text-sm text-n-slate-10">
            {{ $t('REPORT.NO_ENOUGH_DATA') }}
          </span>
        </template>
      </div>
    </div>
    <div
      class="p-4 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-sm font-medium text-n-slate-11 mb-2">
        {{ $t('CUSTOM_DASHBOARD.DAILY_CHART_TITLE') }}
      </h3>
      <div class="h-72">
        <woot-loading-state v-if="isLoadingDailyChart" class="text-xs" />
        <template v-else>
          <LineChart
            v-if="dailySeries.length"
            :collection="dailyChartCollection"
          />
          <span v-else class="text-sm text-n-slate-10">
            {{ $t('REPORT.NO_ENOUGH_DATA') }}
          </span>
        </template>
      </div>
    </div>
  </div>

  <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
    <div
      class="relative p-2 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-sm font-medium text-n-slate-11 px-2 pt-2">
        {{ $t('CUSTOM_DASHBOARD.AGENT_TABLE_TITLE') }}
      </h3>
      <Table :table="agentTable" />
      <woot-loading-state v-if="isLoadingBreakdown" class="text-xs" />
    </div>
    <div
      class="relative p-2 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
    >
      <h3 class="text-sm font-medium text-n-slate-11 px-2 pt-2">
        {{ $t('CUSTOM_DASHBOARD.TEAM_TABLE_TITLE') }}
      </h3>
      <Table :table="teamTable" />
      <woot-loading-state v-if="isLoadingBreakdown" class="text-xs" />
    </div>
  </div>

  <div
    class="relative p-4 mt-4 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <div class="flex items-center justify-between mb-3">
      <h3 class="text-sm font-medium text-n-slate-11">
        {{ $t('CUSTOM_DASHBOARD.LABEL_TABLE_TITLE') }}
      </h3>
      <div v-if="selectedLabelId" class="flex items-center gap-2 text-xs">
        <span class="text-n-slate-11">
          {{ $t('CUSTOM_DASHBOARD.FILTERED_BY', { name: selectedLabelName }) }}
        </span>
        <button
          type="button"
          class="text-n-blue-11 hover:underline"
          @click="selectedLabelId = null"
        >
          {{ $t('CUSTOM_DASHBOARD.CLEAR_FILTER') }}
        </button>
      </div>
    </div>
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div class="lg:col-span-1">
        <div class="h-56">
          <DoughnutChart
            v-if="labelRows.length"
            :collection="labelDoughnutCollection"
            :chart-options="labelDoughnutOptions"
          />
          <span v-else class="text-sm text-n-slate-10">
            {{ $t('REPORT.NO_ENOUGH_DATA') }}
          </span>
        </div>
        <ul class="flex flex-col gap-1.5 mt-3">
          <li
            v-for="row in labelRowsWithShare"
            :key="row.id"
            class="flex items-center gap-2 text-xs cursor-pointer rounded px-1 py-0.5"
            :class="{ 'bg-n-slate-3': selectedLabelId === row.id }"
            @click="toggleLabelFilter(row.id)"
          >
            <span
              class="size-2.5 rounded-full shrink-0"
              :style="{ backgroundColor: row.color || '#64748B' }"
            />
            <span class="text-n-slate-12 truncate flex-1">{{ row.name }}</span>
            <span class="text-n-slate-10">
              {{ row.percentage }}% ({{ row.conversationsCount }})
            </span>
          </li>
        </ul>
      </div>
      <div class="lg:col-span-2">
        <Table :table="labelTable" />
      </div>
    </div>
    <woot-loading-state v-if="isLoadingBreakdown" class="text-xs" />
  </div>

  <div
    v-if="isAdmin"
    class="p-4 mt-4 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <h3 class="text-sm font-medium text-n-slate-11 mb-2">
      {{ $t('CUSTOM_DASHBOARD.MENU_ANALYTICS.TITLE') }}
    </h3>
    <select
      v-model="selectedFlowId"
      class="max-w-xs"
      @change="fetchMenuAnalytics"
    >
      <option value="">
        {{ $t('CUSTOM_DASHBOARD.MENU_ANALYTICS.SELECT_FLOW') }}
      </option>
      <option v-for="flow in flows" :key="flow.id" :value="flow.id">
        {{ flow.name }}
      </option>
    </select>

    <p v-if="!selectedFlowId" class="text-sm text-n-slate-10 mt-3">
      {{ $t('CUSTOM_DASHBOARD.MENU_ANALYTICS.NO_FLOW_SELECTED') }}
    </p>
    <woot-loading-state v-else-if="isLoadingMenuAnalytics" class="text-xs" />
    <p v-else-if="!menuAnalytics.length" class="text-sm text-n-slate-10 mt-3">
      {{ $t('CUSTOM_DASHBOARD.MENU_ANALYTICS.NO_MENU_NODES') }}
    </p>
    <div v-else class="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-3">
      <div v-for="node in menuAnalytics" :key="node.node_id">
        <h4 class="text-xs font-medium text-n-slate-11">
          {{ node.node_label }}
        </h4>
        <div v-if="node.options.some(o => o.count)" class="h-56">
          <BarChart :collection="menuNodeChartCollection(node)" />
        </div>
        <p v-else class="text-sm text-n-slate-10">
          {{ $t('CUSTOM_DASHBOARD.MENU_ANALYTICS.NO_DATA') }}
        </p>
        <p class="text-xs text-n-slate-10 mt-1">
          {{ $t('CUSTOM_DASHBOARD.MENU_ANALYTICS.INVALID_COUNT') }}:
          {{ node.invalid_count }} ·
          {{ $t('CUSTOM_DASHBOARD.MENU_ANALYTICS.TIMEOUT_COUNT') }}:
          {{ node.timeout_count }}
        </p>
      </div>
    </div>
  </div>
</template>
